import axios, { AxiosResponse, AxiosError, InternalAxiosRequestConfig } from 'axios';
import { API_BASE_URL } from '../config/api.config';

// 创建 axios 实例
const instance = axios.create({
  baseURL: API_BASE_URL,
  timeout: 10000,
  headers: {
    'Content-Type': 'application/json'
  }
});

// 请求拦截器
instance.interceptors.request.use(
  (config: InternalAxiosRequestConfig) => {
    // 从 localStorage 获取 token
    const token = localStorage.getItem('access_token');
    if (token) {
      if (!config.headers) config.headers = {} as any;
      (config.headers as Record<string, any>).Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error: AxiosError) => {
    console.error('请求发生错误:', error);
    return Promise.reject(error);
  }
);

// 响应拦截器
instance.interceptors.response.use(
  (response: AxiosResponse) => {
    // 检查响应中的 success 字段
    if (response.data && response.data.success === false) {
      // 如果 success 为 false，将其转换为错误
      return Promise.reject(new Error(response.data.message || '操作失败'));
    }
    return response;
  },
  (error: AxiosError) => {
    // 处理错误响应
    if (error.response) {
      // 服务器返回了错误状态码
      const method = error.config?.method?.toUpperCase() || 'UNKNOWN';
      const path = error.config?.url || '';
      const status = error.response.status;
      const data = error.response.data;
      
      // 对于权限相关的 404 错误，不记录日志（这是正常情况，表示没有权限记录）
      const isPermission404 = status === 404 && (
        path.includes('/api/user-permissions/resource/') ||
        path.includes('/api/user-permissions/batch')
      );
      
      if (!isPermission404) {
        console.error('API请求失败:', {
          method,
          path,
          status,
          data,
          config: error.config,
          headers: error.config?.headers
        });
      }

      switch (error.response.status) {
        case 400:
          error.message = `${method} ${path} - 请求错误 (400): ${JSON.stringify(data)}`;
          break;
        case 401:
          error.message = `${method} ${path} - 未授权，请重新登录 (401): ${JSON.stringify(data)}`;
          break;
        case 403:
          error.message = `${method} ${path} - 拒绝访问 (403): ${JSON.stringify(data)}`;
          break;
        case 404:
          error.message = `${method} ${path} - 请求的资源不存在 (404): ${JSON.stringify(data)}`;
          break;
        case 408:
          error.message = `${method} ${path} - 请求超时 (408): ${JSON.stringify(data)}`;
          break;
        case 500:
          error.message = `${method} ${path} - 服务器内部错误 (500): ${JSON.stringify(data)}`;
          break;
        case 501:
          error.message = `${method} ${path} - 服务未实现 (501): ${JSON.stringify(data)}`;
          break;
        case 502:
          error.message = `${method} ${path} - 网络错误 (502): ${JSON.stringify(data)}`;
          break;
        case 503:
          error.message = `${method} ${path} - 服务不可用 (503): ${JSON.stringify(data)}`;
          break;
        case 504:
          error.message = `${method} ${path} - 网络超时 (504): ${JSON.stringify(data)}`;
          break;
        case 505:
          error.message = `${method} ${path} - HTTP版本不受支持 (505): ${JSON.stringify(data)}`;
          break;
        default:
          error.message = `${method} ${path} - 连接出错 (${status}): ${JSON.stringify(data)}`;
      }
    } else if (error.request) {
      // 请求已经发出，但没有收到响应
      // 检查错误代码以提供更具体的错误信息
      if (error.code === 'ECONNABORTED' || error.message?.includes('timeout')) {
        error.message = '请求超时，请检查网络连接后重试';
      } else if (error.code === 'ERR_NETWORK' || error.code === 'NETWORK_ERROR') {
        error.message = '网络连接失败，请检查网络设置';
      } else if (error.code === 'ECONNREFUSED') {
        error.message = '无法连接到服务器，请检查服务器是否正常运行';
      } else {
        const url = error.config?.url || '未知地址';
        error.message = `网络连接失败，无法访问 ${url}，请检查网络连接`;
      }
    } else {
      // 请求配置出错
      error.message = `请求配置出错: ${error.message}`;
    }
    
    return Promise.reject(error);
  }
);

export default instance; 